// 避免重复声明
if (!window.ScrollAnimation) {
    window.ScrollAnimation = class extends HTMLElement {
    constructor() {
      super();
      this.animationMap = new Map();
      this.isInitialized = false;
      this.animationItems = [];
      this.container = null;
      this.boundUpdateStyles = this.updateStyles.bind(this);
      this.boundUpdateAnimationMap = this.updateAnimationMap.bind(this);
      
      // 自动重计算相关
      this.lastDocumentHeight = 0;
      this.lastContainerHeight = 0;
      this.stabilityCheckCount = 0;
      this.maxStabilityChecks = 10;
      this.retryCount = 0;
      this.maxRetries = 5;
  
      // 默认配置
      this.config = {
        usableRangePercent: 0.8,
        defaultDurationPercent: 1,
        allowOverlap: true,
        overlapIntensity: 0.4,
        startOffsetPercent: 0,
        endBufferPx: 0,
        animationEasing: 'linear'
      };
    }
  
    connectedCallback() {
      if (!this.isInitialized) {
        this.parseAttributes();
        this.smartInit();
      }
    }
  
    parseAttributes() {
      const usableRange = this.getAttribute('usable-range');
      if (usableRange) this.config.usableRangePercent = parseFloat(usableRange);
  
      const defaultDuration = this.getAttribute('default-duration');
      if (defaultDuration) this.config.defaultDurationPercent = parseFloat(defaultDuration);
  
      const allowOverlap = this.getAttribute('allow-overlap');
      if (allowOverlap !== null) this.config.allowOverlap = allowOverlap !== 'false';
  
      const overlapIntensity = this.getAttribute('overlap-intensity');
      if (overlapIntensity) this.config.overlapIntensity = parseFloat(overlapIntensity);
  
      const startOffset = this.getAttribute('start-offset');
      if (startOffset) this.config.startOffsetPercent = parseFloat(startOffset);
  
      const endBuffer = this.getAttribute('end-buffer');
      if (endBuffer) this.config.endBufferPx = parseFloat(endBuffer);
  
      const easing = this.getAttribute('easing');
      if (easing) this.config.animationEasing = easing;
    }
  
    // 智能初始化 - 等待内容稳定后再初始化
    smartInit() {
      // 立即尝试初始化
      this.tryInit();
      
      // 设置多重检查机制
      this.setupContentMonitoring();
    }
  
    setupContentMonitoring() {
      // 1. 监听 load 事件（图片等资源）
      const images = this.querySelectorAll('img');
      let loadedImages = 0;
      const totalImages = images.length;
      
      if (totalImages > 0) {
        images.forEach(img => {
          const handleImageLoad = () => {
            loadedImages++;
            if (loadedImages === totalImages) {
              // 所有图片加载完成，延迟重新计算
              setTimeout(() => this.recheckAndUpdate(), 100);
            }
          };
          
          if (img.complete) {
            handleImageLoad();
          } else {
            img.addEventListener('load', handleImageLoad, { once: true });
            img.addEventListener('error', handleImageLoad, { once: true });
          }
        });
      }
  
      // 2. 监听字体加载
      if (document.fonts) {
        document.fonts.ready.then(() => {
          setTimeout(() => this.recheckAndUpdate(), 100);
        });
      }
  
      // 3. 定期检查文档高度变化
      this.startPeriodicCheck();
  
      // 4. 监听窗口 load 事件
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => {
          setTimeout(() => this.recheckAndUpdate(), 200);
        }, { once: true });
      }
  
      // 5. MutationObserver 监听 DOM 变化
      if (window.MutationObserver) {
        const observer = new MutationObserver(() => {
          clearTimeout(this.mutationTimeout);
          this.mutationTimeout = setTimeout(() => {
            this.recheckAndUpdate();
          }, 300);
        });
        
        observer.observe(this, {
          childList: true,
          subtree: true,
          attributes: false
        });
        
        this.mutationObserver = observer;
      }
    }
  
    startPeriodicCheck() {
      let checkCount = 0;
      const maxChecks = 20; // 最多检查20次
      
      const periodicCheck = () => {
        if (checkCount >= maxChecks || !document.body.contains(this)) {
          return;
        }
        
        const currentDocHeight = document.documentElement.scrollHeight;
        const currentContainerHeight = this.container ? this.container.scrollHeight : 0;
        
        // 检查高度是否发生显著变化
        const heightChanged = Math.abs(currentDocHeight - this.lastDocumentHeight) > 50 ||
                             Math.abs(currentContainerHeight - this.lastContainerHeight) > 20;
        
        if (heightChanged && this.isInitialized) {
          this.lastDocumentHeight = currentDocHeight;
          this.lastContainerHeight = currentContainerHeight;
          this.recheckAndUpdate();
        }
        
        checkCount++;
        setTimeout(periodicCheck, 500); // 每500ms检查一次
      };
      
      setTimeout(periodicCheck, 500);
    }
  
    tryInit() {
      this.container = this.querySelector('.animation-container');
      this.animationItems = Array.from(this.querySelectorAll('.animation-item'));
  
  
      if (!this.container || this.animationItems.length === 0) {
        // 如果还没准备好，稍后重试
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => this.tryInit(), 200);
        }
        return;
      }
  
      // 检查容器是否有有效尺寸
      const containerRect = this.container.getBoundingClientRect();
      const playgroundRect = this.getBoundingClientRect();
      
      if (containerRect.height <= 0 || playgroundRect.height <= 0) {
        // 尺寸无效，稍后重试
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => this.tryInit(), 200);
        }
        return;
      }
  
      // 记录初始高度
      this.lastDocumentHeight = document.documentElement.scrollHeight;
      this.lastContainerHeight = this.container.scrollHeight;
  
      this.updateAnimationMap();
      this.updateStyles();
  
      // 添加事件监听
      window.addEventListener('scroll', this.boundUpdateStyles, { passive: true });
      window.addEventListener('resize', this.handleResize.bind(this), { passive: true });
  
      this.isInitialized = true;
      this.retryCount = 0; // 重置重试计数
    }
  
    // 重新检查并更新
    recheckAndUpdate() {
      if (!this.isInitialized) {
        this.tryInit();
        return;
      }
  
      // 检查当前尺寸是否有效
      const containerRect = this.container.getBoundingClientRect();
      const playgroundRect = this.getBoundingClientRect();
      
      if (containerRect.height <= 0 || playgroundRect.height <= 0) {
        // 尺寸仍然无效，延迟重试
        setTimeout(() => this.recheckAndUpdate(), 300);
        return;
      }
  
      // 更新动画映射
      this.updateAnimationMap();
    }
  
    handleResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.recheckAndUpdate();
      }, 150);
    }
  
    disconnectedCallback() {
      this.cleanup();
    }
  
    cleanup() {
      window.removeEventListener('scroll', this.boundUpdateStyles);
      window.removeEventListener('resize', this.handleResize);
      
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
      
      clearTimeout(this.resizeTimeout);
      clearTimeout(this.mutationTimeout);
      
      this.animationMap.clear();
      this.isInitialized = false;
    }
  
    createAnimation(xStart, xEnd, yStart, yEnd) {
      return function (x) {
        if (x <= xStart) return yStart;
        if (x >= xEnd) return yEnd;
        return yStart + ((x - xStart) / (xEnd - xStart)) * (yEnd - yStart);
      };
    }
  
    parseAnimationData(dataString) {
      if (!dataString) return {};
  
      const properties = {};
      const pairs = dataString.split(',');
  
      pairs.forEach(pair => {
        const [key, value] = pair.split(':').map(s => s.trim());
        if (key && value !== undefined) {
          if (value.includes('%')) {
            properties[key] = parseFloat(value.replace('%', ''));
            properties[key + '_unit'] = '%';
          } else {
            properties[key] = parseFloat(value) || 0;
          }
        }
      });
  
      return properties;
    }
  
    updateAnimationMap() {
      this.animationMap.clear();
  
      if (this.animationItems.length === 0) return;
  
      // 获取最新尺寸 - 多次尝试确保准确性
      let playGroundRect, containerRect;
      let attempts = 0;
      
      do {
        playGroundRect = this.getBoundingClientRect();
        containerRect = this.container.getBoundingClientRect();
        attempts++;
        
        // 如果尺寸无效且尝试次数不多，稍等再试
        if ((playGroundRect.height <= 0 || containerRect.height <= 0) && attempts < 3) {
          // 强制重新计算布局
          this.offsetHeight; // 触发重新计算
          continue;
        }
        break;
      } while (attempts < 3);
  
      // 最终尺寸检查
      if (playGroundRect.height <= 0 || containerRect.height <= 0) {
        // 延迟重试
        setTimeout(() => this.updateAnimationMap(), 200);
        return;
      }
  
      const scrollY = window.scrollY;

      // 动画范围计算
      // 动画应该在 container 吸顶期间完成
      // 吸顶开始：playground 顶部到达视口顶部
      // 吸顶结束：playground 底部到达视口底部
      const playGroundHeight = playGroundRect.height;
      const containerHeight = containerRect.height;
      const viewportHeight = window.innerHeight;
      const animationStartPoint = playGroundRect.top + scrollY;
      const playGroundBottom = playGroundRect.bottom + scrollY;

      // 计算总动画范围：playground 高度减去视口高度
      // 这是 container 从开始吸顶到结束吸顶的滚动距离
      const totalAnimationRange = playGroundHeight - viewportHeight;

      // 动画结束点：playground 底部到达视口底部的位置
      const animationEndPoint = playGroundBottom - viewportHeight;

      // 范围有效性检查
      if (totalAnimationRange <= 100) { // 至少需要100px的动画范围
        setTimeout(() => this.updateAnimationMap(), 200);
        return;
      }

      // 应用配置
      // usableRange 现在直接基于计算出的总范围
      const usableRange = totalAnimationRange * this.config.usableRangePercent;
      const startOffset = totalAnimationRange * this.config.startOffsetPercent;
      const effectiveAnimationStart = animationStartPoint + startOffset;
      // 确保有效结束点不超过动画结束点，保证动画在 playground 离开视口前完成
      const effectiveAnimationEnd = Math.min(
        effectiveAnimationStart + usableRange,
        animationEndPoint
      );
  
      // 获取所有不同的order值并排序
      const orders = [...new Set(this.animationItems.map(item =>
        parseFloat(item.dataset.order || 0)
      ))].sort((a, b) => a - b);
  
      const totalSteps = Math.max(orders.length, 1);
      const stepDuration = usableRange / totalSteps;
  
      this.animationItems.forEach((item, index) => {
        const delay = parseFloat(item.dataset.delay || 0);
        const order = parseFloat(item.dataset.order || index);
        const orderIndex = orders.indexOf(order);
        const isContinuous = item.dataset.continuous === 'true';
  
        // 计算动画持续时间
        let customDuration;
        if (isContinuous) {
          customDuration = usableRange;
        } else if (item.dataset.duration) {
          customDuration = parseFloat(item.dataset.duration);
        } else {
          customDuration = stepDuration * this.config.defaultDurationPercent;
        }
  
        // 重叠处理逻辑
        if (!isContinuous) {
          if (this.config.allowOverlap) {
            const overlapAmount = stepDuration * this.config.overlapIntensity;
            customDuration = Math.max(customDuration, stepDuration + overlapAmount);
          } else {
            customDuration = Math.min(customDuration, stepDuration * 0.95);
          }
        }
  
        // 计算动画位置
        let groupStartPoint;
        if (isContinuous) {
          groupStartPoint = effectiveAnimationStart;
        } else if (this.config.allowOverlap && orderIndex > 0) {
          const adjustedStepDuration = stepDuration * (1 - this.config.overlapIntensity * 0.2);
          groupStartPoint = effectiveAnimationStart + (orderIndex * adjustedStepDuration);
        } else {
          groupStartPoint = effectiveAnimationStart + (orderIndex * stepDuration);
        }
        
        const scrollStart = groupStartPoint + delay;
        let itemScrollEnd = scrollStart + customDuration;
        
        // 确保所有动画都在 animationEndPoint 之前完成
        // animationEndPoint 是 playground 底部离开视口的精确位置
        itemScrollEnd = Math.min(itemScrollEnd, animationEndPoint);

        // 确保最小持续时间，但不超过动画结束点
        const minDuration = Math.min(stepDuration * 0.2, 100); // 至少100px或20%步长
        const minEndPoint = scrollStart + minDuration;
        if (itemScrollEnd < minEndPoint) {
          itemScrollEnd = Math.min(minEndPoint, animationEndPoint);
        }
        
        // 最终确保不超过动画结束点
        itemScrollEnd = Math.min(itemScrollEnd, animationEndPoint);
  
        const fromData = this.parseAnimationData(item.dataset.from || '');
        const toData = this.parseAnimationData(item.dataset.to || '');
  
        const animations = this.createItemAnimations(scrollStart, itemScrollEnd, fromData, toData);
        this.animationMap.set(item, animations);
      });
  
      // 更新当前样式
      this.updateStyles();
    }
  
    createItemAnimations(scrollStart, scrollEnd, fromData, toData) {
      const animations = {};
      const transformKeys = ['translateX', 'translateY', 'translateZ', 'rotateX', 'rotateY', 'rotateZ', 'scale', 'scaleX', 'scaleY', 'skewX', 'skewY'];
      const transformAnimations = {};
  
      Object.keys({ ...fromData, ...toData }).forEach(key => {
        if (key.endsWith('_unit')) return;
  
        const fromValue = fromData[key] || (transformKeys.includes(key) ? (key.includes('scale') ? 1 : 0) : 0);
        const toValue = toData[key] || (transformKeys.includes(key) ? (key.includes('scale') ? 1 : 0) : 0);
        const fromUnit = fromData[key + '_unit'] || 'px';
        const toUnit = toData[key + '_unit'] || 'px';
  
        if (transformKeys.includes(key)) {
          transformAnimations[key] = this.createAnimation(scrollStart, scrollEnd, fromValue, toValue);
          transformAnimations[key + '_unit'] = fromUnit || toUnit;
        } else {
          animations[key] = (scrollY) => {
            const value = this.createAnimation(scrollStart, scrollEnd, fromValue, toValue)(scrollY);
            return key === 'opacity' ? Math.max(0, Math.min(1, value)) : value;
          };
        }
      });
  
      if (Object.keys(transformAnimations).length > 0) {
        animations.transform = (scrollY) => {
          const transforms = [];
  
          if (transformAnimations.translateX || transformAnimations.translateY || transformAnimations.translateZ) {
            const x = transformAnimations.translateX ? this.formatTransformValue(transformAnimations.translateX(scrollY), 'translateX', transformAnimations.translateX_unit) : '0px';
            const y = transformAnimations.translateY ? this.formatTransformValue(transformAnimations.translateY(scrollY), 'translateY', transformAnimations.translateY_unit) : '0px';
            const z = transformAnimations.translateZ ? this.formatTransformValue(transformAnimations.translateZ(scrollY), 'translateZ', transformAnimations.translateZ_unit) : '0px';
            transforms.push(`translate3d(${x}, ${y}, ${z})`);
          }
  
          if (transformAnimations.rotateX || transformAnimations.rotateY || transformAnimations.rotateZ) {
            if (transformAnimations.rotateX) transforms.push(`rotateX(${transformAnimations.rotateX(scrollY)}deg)`);
            if (transformAnimations.rotateY) transforms.push(`rotateY(${transformAnimations.rotateY(scrollY)}deg)`);
            if (transformAnimations.rotateZ) transforms.push(`rotateZ(${transformAnimations.rotateZ(scrollY)}deg)`);
          }
  
          if (transformAnimations.scale) {
            transforms.push(`scale(${transformAnimations.scale(scrollY)})`);
          } else {
            if (transformAnimations.scaleX) transforms.push(`scaleX(${transformAnimations.scaleX(scrollY)})`);
            if (transformAnimations.scaleY) transforms.push(`scaleY(${transformAnimations.scaleY(scrollY)})`);
          }
  
          if (transformAnimations.skewX) transforms.push(`skewX(${transformAnimations.skewX(scrollY)}deg)`);
          if (transformAnimations.skewY) transforms.push(`skewY(${transformAnimations.skewY(scrollY)}deg)`);
  
          return transforms.join(' ');
        };
      }
  
      return animations;
    }
  
    formatTransformValue(value, property, unit) {
      if (unit === '%') {
        return `${value}%`;
      }
      return `${value}px`;
    }
  
    updateStyles() {
      const scrollY = window.scrollY;
  
      for (const [item, animations] of this.animationMap) {
        for (const prop in animations) {
          const value = animations[prop](scrollY);
          if (prop === 'transform') {
            item.style.transform = value;
          } else {
            item.style[prop] = typeof value === 'number' ? value : value;
          }
        }
      }
    }
    }
  }
  
  if (!customElements.get('scroll-animation')) {
    customElements.define('scroll-animation', window.ScrollAnimation);
  }